import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, withLatestFrom } from 'rxjs/operators';

import { getCurrentIntervention } from '../../../interventions/store/selectors/interventions';
import { HttpDeviceGateway } from '../../services/HttpDeviceGateway';
import { DeviceActions } from '../actions/device.actions';
import { SiteDetailPageActions } from '../actions/site-detail-page.actions';
import { getDiagnosticSections, getRawDevice, getSettingsPropertyIds } from '../selectors/device-detail';

@Injectable({ providedIn: 'root' })
export class SiteDetailDeviceUpdateEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly deviceGateway = inject(HttpDeviceGateway);

    submitDeviceUpdate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SiteDetailPageActions.deviceUpdateRequested),
            switchMap((updateRequest) =>
                combineLatest([
                    this.store.select(getRawDevice(updateRequest.deviceId)),
                    this.store.select(getCurrentIntervention),
                ]).pipe(
                    take(1),
                    map(([device, intervention]) => ({
                        device,
                        property: updateRequest.property,
                        newValue: updateRequest.newValue,
                        sessionId: intervention.sessionId,
                    })),
                ),
            ),
            mergeMap((updateCommand) =>
                this.deviceGateway.updateDevice(updateCommand).pipe(
                    map((pendingTask) => DeviceActions.deviceUpdateStarted({ pendingTask, command: updateCommand })),
                    catchError(() => of(DeviceActions.deviceUpdateFailed(updateCommand))),
                ),
            ),
        ),
    );

    refreshSettingsSection$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SiteDetailPageActions.settingsRefreshRequested),
            withLatestFrom(this.store.select(getSettingsPropertyIds)),
            filter(([, propertyIds]) => propertyIds.length > 0),
            mergeMap(([{ deviceId, sectionTitle }, propertyIds]) =>
                this.deviceGateway.refreshDeviceStates(deviceId, propertyIds).pipe(
                    map((pendingTasks) =>
                        SiteDetailPageActions.settingsRefreshStarted({ deviceId, sectionTitle, pendingTasks }),
                    ),
                    catchError(() => of(SiteDetailPageActions.settingsRefreshFailed({ deviceId, sectionTitle }))),
                ),
            ),
        ),
    );

    refreshDiagnosticSection$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SiteDetailPageActions.diagnosticSectionRefreshRequested),
            withLatestFrom(this.store.select(getDiagnosticSections)),
            filter(([, sections]) => !!sections?.length),
            mergeMap(([{ deviceId, sectionTitle }, sections]) => {
                const propertyIds = sections!.find((s) => s.title === sectionTitle)?.propertyIds ?? [];
                if (propertyIds.length === 0) return [];

                return this.deviceGateway.refreshDeviceStates(deviceId, propertyIds).pipe(
                    map((pendingTasks) =>
                        SiteDetailPageActions.diagnosticSectionRefreshStarted({ deviceId, sectionTitle, pendingTasks }),
                    ),
                    catchError(() =>
                        of(SiteDetailPageActions.diagnosticSectionRefreshFailed({ deviceId, sectionTitle })),
                    ),
                );
            }),
        ),
    );
}
